import { client as axios } from "./axiosClient";
import { Constants } from "@socialcoach/services";
import { catchServiceException } from "@socialcoach/services";
import * as DTO from "../interfaces";
import { AccessUser } from "../interfaces";
import { EligibilityCriterion } from "./bedrock";

export type AccessType = "CAMPAIGNS" | "LIBRARY" | "VIDEO_PAGES";

export interface GenericAccess {
  eligibilityCriteria?: EligibilityCriterion[];
  eligiblePlayerIds?: number[];
  accessUsers?: AccessUser[];
}

const COACH_SERVICE = `${Constants.API_URL}/coaches`;
const ACCOUNT_SERVICE = `${Constants.API_URL}/accounts`;

export class CoachService {
  @catchServiceException()
  static async updateCoachStatus(coachId: string, status: DTO.CoachStatuses): Promise<void> {
    const { data } = await axios.put<void>(
      `${ACCOUNT_SERVICE}/coaches/${coachId}/status/${status}`,
      {},
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async getPlans(coachId: string | number): Promise<DTO.Plan[]> {
    const { data } = await axios.get<DTO.Plan[]>(`${COACH_SERVICE}/${coachId}/price-plans`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async deleteBankInfo(coachId: string, bankId: number): Promise<any> {
    const { data } = await axios.delete<DTO.LandingResponse>(
      `${ACCOUNT_SERVICE}/coaches/${coachId}/bank-accounts/${bankId}`,
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async statistics(coachId: string | number): Promise<DTO.StatisticResponse> {
    const { data } = await axios.get<DTO.StatisticResponse>(`${COACH_SERVICE}/statistics/search/${coachId}`, {
      withCredentials: true,
    });
    return data;
  }

  static async createPaymentIntent(playerId: number): Promise<string> {
    const response = await axios.post<{ clientSecret: string }>(`${COACH_SERVICE}/payments`, {
      playerId,
    });
    return response.data.clientSecret;
  }

  @catchServiceException()
  static async updatePaymentMethod(request: DTO.UpdatePaymentMethodRequest): Promise<void> {
    await axios.put(`${COACH_SERVICE}/payments/customers`, request);
  }

  @catchServiceException()
  static async branches(coachId: string | number): Promise<DTO.Branch[]> {
    const { data } = await axios.get<DTO.Branch[]>(`${COACH_SERVICE}/coaches/${coachId}/branches`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async states(coachId: string | number): Promise<DTO.State[]> {
    const { data } = await axios.get<DTO.State[]>(`${COACH_SERVICE}/coaches/${coachId}/states`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async costCenters(coachId: string | number): Promise<DTO.CostCenter[]> {
    const { data } = await axios.get<DTO.CostCenter[]>(`${COACH_SERVICE}/coaches/${coachId}/costCenters`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async industryRoles(coachId: string | number): Promise<DTO.IndustryRole[]> {
    const { data } = await axios.get<DTO.IndustryRole[]>(`${COACH_SERVICE}/coaches/${coachId}/industryRoles`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async playerIndustryRoles(coachId: number | string, playerId: number): Promise<DTO.PlayerIndustryRoles> {
    const { data } = await axios.get<DTO.PlayerIndustryRoles>(
      `${COACH_SERVICE}/coaches/${coachId}/players/${playerId}/industryRoles`,
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async updatePlayerIndustryRole(
    coachId: number,
    playerId: number,
    industryRole: string | undefined
  ): Promise<void> {
    const industryRoles = industryRole ? [industryRole] : [];
    await axios.put<void>(
      `${COACH_SERVICE}/coaches/${coachId}/players/${playerId}/industryRoles`,
      {
        industryRoles,
      },
      {
        withCredentials: true,
      }
    );
  }

  @catchServiceException()
  static async regions(coachId: string | number): Promise<DTO.Region[]> {
    const { data } = await axios.get<DTO.Region[]>(`${COACH_SERVICE}/coaches/${coachId}/regions`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async getAccessPreview(
    coachId: string | number,
    resourceType: AccessType,
    criterion: EligibilityCriterion[]
  ): Promise<GenericAccess> {
    const response = await axios.post<GenericAccess>(
      `${COACH_SERVICE}/coaches/${coachId}/access/preview`,
      {
        eligibilityCriteria: criterion,
        resourceType,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async getById(coachId: string): Promise<DTO.CompleteAccount> {
    const { data } = await axios.get<DTO.CompleteAccount>(`${ACCOUNT_SERVICE}/coaches/${coachId}`, {
      withCredentials: true,
    });
    return data;
  }
}
